import type { TopicFamily, TopicOf } from "@trader/shared/topics/index"
import { type ClientMessage, WS_PATH } from "@trader/shared/WsProtocol"
import { useSyncExternalStore } from "react"
import { decodeFrame, type TopicHandlers, TopicRouter } from "./liveTopics.ts"

// A single app-wide WebSocket. Messages mutate plain state; subscribers are
// notified at most once per animation frame, so a burst of ticks costs one
// React render instead of one per message.
//
// Live topics (subscribeTopic) share the socket: one server subscription per
// key, resubscribed after a reconnect, a seq gap or a Resync (liveTopics.ts).
// Their handlers run as frames arrive; a store that feeds React should batch its
// notifications with `frameBatcher`.

export type { TopicHandlers } from "./liveTopics.ts"

export type LiveStatus = "connecting" | "open" | "closed"

export interface LiveSnapshot {
  readonly status: LiveStatus
  readonly serverVersion: string | null
  readonly rttMs: number | null
  readonly clockOffsetMs: number | null
  readonly heartbeatSeq: number | null
}

const PING_INTERVAL_MS = 2_000
const MAX_BACKOFF_MS = 5_000

/** Calls `flush` at most once per animation frame, however often the returned function is called. */
export const frameBatcher = (flush: () => void): (() => void) => {
  let frame = 0
  return () => {
    if (frame !== 0) return
    frame = requestAnimationFrame(() => {
      frame = 0
      flush()
    })
  }
}

class LiveConnection {
  private socket: WebSocket | null = null
  private pingTimer: ReturnType<typeof setInterval> | undefined
  private reconnectAttempt = 0
  private readonly listeners = new Set<() => void>()
  private readonly notify = frameBatcher(() => {
    for (const listener of this.listeners) listener()
  })
  private snapshot: LiveSnapshot = {
    status: "connecting",
    serverVersion: null,
    rttMs: null,
    clockOffsetMs: null,
    heartbeatSeq: null
  }

  private readonly url: string
  private readonly topics: TopicRouter

  constructor(url: string) {
    this.url = url
    this.topics = new TopicRouter({
      send: (text) => this.sendText(text),
      schedule: (run, delayMs) => {
        const handle = setTimeout(run, delayMs)
        return { cancel: () => clearTimeout(handle) }
      },
      now: () => Date.now(),
      onDecodeError: (key, data) => console.error(`live: ${key} sent data this client can't decode; resubscribing`, data),
      onListenerError: (key, error) => console.error(`live: a ${key} listener failed`, error)
    })
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    this.ensureConnected()
    return () => this.listeners.delete(listener)
  }

  readonly getSnapshot = (): LiveSnapshot => this.snapshot

  /** Listens to a topic until the returned function is called. */
  subscribeTopic<F extends TopicFamily>(family: F, topic: TopicOf<F>, handlers: TopicHandlers<F>): () => void {
    this.ensureConnected()
    return this.topics.subscribe(family, topic, handlers)
  }

  private ensureConnected(): void {
    if (this.socket === null) this.connect()
  }

  private connect(): void {
    const socket = new WebSocket(this.url)
    this.socket = socket
    this.update({ status: "connecting" })

    socket.onopen = () => {
      this.reconnectAttempt = 0
      this.update({ status: "open" })
      this.ping()
      this.pingTimer = setInterval(() => this.ping(), PING_INTERVAL_MS)
      this.topics.connectionOpened()
    }
    socket.onmessage = (event: MessageEvent<unknown>) => this.onMessage(event.data)
    socket.onclose = () => {
      clearInterval(this.pingTimer)
      this.topics.connectionClosed()
      this.update({ status: "closed", rttMs: null })
      const backoff = Math.min(250 * 2 ** this.reconnectAttempt++, MAX_BACKOFF_MS)
      setTimeout(() => this.connect(), backoff + Math.random() * 250)
    }
  }

  private onMessage(data: unknown): void {
    const message = decodeFrame(data)
    if (message === undefined) return // not a message this client knows (a newer server)
    switch (message._tag) {
      case "Welcome":
        this.update({ serverVersion: message.version })
        break
      case "Pong": {
        const rttMs = performance.now() - message.t
        this.update({ rttMs, clockOffsetMs: message.serverTime - (Date.now() - rttMs / 2) })
        break
      }
      case "Heartbeat":
        this.update({ heartbeatSeq: message.seq })
        break
      default:
        this.topics.receive(message)
    }
  }

  private ping(): void {
    this.send({ _tag: "Ping", t: performance.now() })
  }

  /** Ping is plain numbers, so its JSON is its wire form. */
  private send(message: Extract<ClientMessage, { readonly _tag: "Ping" }>): void {
    this.sendText(JSON.stringify(message))
  }

  private sendText(text: string): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(text)
  }

  private update(patch: Partial<LiveSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch }
    this.notify()
  }
}

const wsUrl = `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}${WS_PATH}`

export const live = new LiveConnection(wsUrl)

export const useLive = (): LiveSnapshot => useSyncExternalStore(live.subscribe, live.getSnapshot)

/**
 * Listens to a live topic: `onSnapshot` first (and again after any resync), then
 * `onUpdate` in seq order, plus `onStatus`. Returns the unsubscribe function.
 * Subscriptions to one key share a single server subscription.
 *
 * ```ts
 * const stop = subscribeTopic(AccountTopic, { _tag: "Account", accountId }, { onSnapshot, onUpdate, onStatus })
 * ```
 */
export const subscribeTopic = <F extends TopicFamily>(
  family: F,
  topic: TopicOf<F>,
  handlers: TopicHandlers<F>
): (() => void) => live.subscribeTopic(family, topic, handlers)
